/**
 * Confluence Integration Component - S002
 * Configure Confluence to auto-publish markdown docs
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/apiClient';

interface ConfluenceConfig {
  organization_id: string;
  confluence_url: string;
  email: string;
  default_space_key: string;
  enabled: boolean;
  auto_publish_docs: boolean;
  sync_status_back: boolean;
  configured: boolean;
  created_at: string;
  updated_at: string;
}

interface ConfluenceProject {
  key: string;
  name: string;
  id: string;
}

export function ConfluenceIntegration() {
  const [config, setConfig] = useState<ConfluenceConfig | null>(null);
  const [confluenceUrl, setConfluenceUrl] = useState('');
  const [email, setEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [projects, setProjects] = useState<ConfluenceProject[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [autoCreate, setAutoCreate] = useState(true);
  const [syncBack, setSyncBack] = useState(true);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await apiClient.get('/api/v1/integrations/confluence');
      setConfig(response.data);
      setConfluenceUrl(response.data.confluence_url);
      setEmail(response.data.email);
      setProjectKey(response.data.default_space_key);
      setEnabled(response.data.enabled);
      setAutoCreate(response.data.auto_publish_docs);
      setSyncBack(response.data.sync_status_back);
      
      // Load projects if configured
      if (response.data.configured) {
        loadProjects();
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Failed to load Confluence config:', err);
      }
    }
  };

  const loadProjects = async () => {
    try {
      const response = await apiClient.get('/api/v1/integrations/confluence/projects');
      setProjects(response.data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const handleValidate = async () => {
    if (!confluenceUrl || !email || !apiToken) {
      setError('Please fill in all required fields');
      return;
    }

    setValidating(true);
    setError(null);

    try {
      await apiClient.post('/api/v1/integrations/confluence/validate', {
        confluence_url: confluenceUrl,
        email,
        api_token: apiToken
      });
      
      setSuccess('Credentials validated! You can now save the configuration.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to validate credentials');
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.post('/api/v1/integrations/confluence', {
        confluence_url: confluenceUrl,
        email,
        api_token: apiToken,
        default_space_key: projectKey,
        enabled,
        auto_publish_docs: autoCreate,
        sync_status_back: syncBack
      });

      setSuccess('Confluence configuration saved!');
      setApiToken(''); // Clear token
      await loadConfig();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setLoading(true);
    try {
      await apiClient.patch(`/api/v1/integrations/confluence/toggle?enabled=${!enabled}`);
      setSuccess(`Confluence ${!enabled ? 'enabled' : 'disabled'}`);
      await loadConfig();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Remove Confluence integration?')) return;
    
    setLoading(true);
    try {
      await apiClient.delete('/api/v1/integrations/confluence');
      setSuccess('Confluence integration removed');
      setConfig(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-black dark:text-white">
          Confluence Configuration
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Auto-create Confluence tickets when alerts are triggered
        </p>
      </div>

      {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded">{error}</div>}
      {success && <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded">{success}</div>}

      {config && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-blue-900">Confluence is configured</p>
              <p className="text-sm text-blue-700">
                {config.confluence_url} | Project: {config.default_space_key} | {config.enabled ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={handleToggle} disabled={loading} className={`px-4 py-2 rounded ${config.enabled ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'} text-white disabled:opacity-50`}>
                {config.enabled ? 'Disable' : 'Enable'}
              </button>
              <button onClick={handleDelete} disabled={loading} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded disabled:opacity-50">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Confluence URL <span className="text-red-500">*</span>
          </label>
          <input type="url" value={confluenceUrl} onChange={(e) => setConfluenceUrl(e.target.value)} placeholder="https://yourcompany.atlassian.net" className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white" required={!config} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white" required={!config} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Token <span className="text-red-500">*</span>
          </label>
          <input type="password" value={apiToken} onChange={(e) => setApiToken(e.target.value)} placeholder="Enter Confluence API token" className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white" required={!config} />
          <p className="text-xs text-gray-500 mt-1">Generate at: Confluence Settings → Security → API tokens</p>
        </div>

        <div>
          <button type="button" onClick={handleValidate} disabled={validating} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded disabled:opacity-50">
            {validating ? 'Validating...' : 'Validate Credentials'}
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Default Project <span className="text-red-500">*</span>
          </label>
          <input type="text" value={projectKey} onChange={(e) => setProjectKey(e.target.value)} placeholder="SPACE-KEY" className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white" required />
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} className="h-5 w-5" />
            <div><p className="font-medium text-sm">Auto-create tickets from alerts</p></div>
          </label>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={syncBack} onChange={(e) => setSyncBack(e.target.checked)} className="h-5 w-5" />
            <div><p className="font-medium text-sm">Sync Confluence status back to alerts</p></div>
          </label>
        </div>

        <button type="submit" disabled={loading || (!apiToken && !config)} className="w-full rounded bg-primary py-3 px-6 font-medium text-gray hover:bg-opacity-90 disabled:opacity-50">
          {loading ? 'Saving...' : config ? 'Update' : 'Save'}
        </button>
      </form>
    </div>
  );
}
